import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createMedia, completeMedia, putMediaThumb } from '../../api/media.ts';
import { uploadToUrl } from '../../api/client.ts';
import { generateThumb } from '../../lib/thumbs.ts';
import { performUpload, inferMediaKind, isTerminal, type UploadItem } from '../../lib/upload.ts';

/** Drives the presigned upload flow for a set of dropped files, tracking per-file state. */
export function useUploads() {
  const qc = useQueryClient();
  const [items, setItems] = useState<UploadItem[]>([]);

  const patch = (id: string, p: Partial<UploadItem>) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...p } : i)));

  function start(files: File[]) {
    for (const file of files) {
      const localId = crypto.randomUUID();
      const kind = inferMediaKind(file);
      if (!kind) {
        setItems((prev) => [
          {
            id: localId,
            filename: file.name,
            size_bytes: file.size,
            kind: 'image',
            phase: 'error',
            progress: 0,
            error: 'Unsupported file — images and video only',
          },
          ...prev,
        ]);
        continue;
      }
      setItems((prev) => [
        { id: localId, filename: file.name, size_bytes: file.size, kind, phase: 'queued', progress: 0 },
        ...prev,
      ]);
      void performUpload({
        file,
        kind,
        deps: { createMedia, uploadToUrl, completeMedia, generateThumb, putThumb: putMediaThumb },
        onPhase: (p) => patch(localId, p),
      })
        .then(() => qc.invalidateQueries({ queryKey: ['media'] }))
        .catch(() => {
          /* phase already set to 'error' by performUpload */
        });
    }
  }

  const clearFinished = () => setItems((prev) => prev.filter((i) => !isTerminal(i.phase)));

  return { items, start, clearFinished };
}
